import React, {Fragment} from 'react'
import diff from 'deep-diff'
import {useParams, useHistory} from 'react-router-dom'
import moment from 'admin/lib/moment'
import {toParams, formatNumber} from 'admin/lib/format'
import {dateIsInThePast, dateIsInTheFuture} from 'admin/lib/moment'
import useRepeatingInvoice from 'admin/hooks/useRepeatingInvoice'
import useOrganisation from 'admin/hooks/useOrganisation'
import useEffect from 'admin/hooks/useEffect'
import useDocument from 'admin/hooks/useDocument'
import useBoolState from 'admin/hooks/useBoolState'
import useStateObject from 'admin/hooks/useStateObject'
import Confirmation from 'admin/components/Confirmation'
import Flash from 'admin/components/shared/Flash'
import History from 'admin/components/History'
import Form from './Form'
import Email from 'admin/components/shared/Email'
import Deletion from '../Deletion'
import Header from '../shared/Header'

const EMAIL_DEFAULTS = {
  subject: `[Invoice Month] Invoice [Invoice Number] from [Organisation Name] for [Contact Name]`,
  message: `Hi [Contact First Name],

Here's your [Invoice Month] invoice [Invoice Number] for $NZD [Amount], due on [Due Date].

If you have any questions, please let us know.

Ngā mihi,
[Organisation Name]`
}

export default () => {
  const history = useHistory()
  const {organisationID, repeatingInvoiceID} = useParams()
  const {organisation} = useOrganisation(organisationID)
  const {repeatingInvoice = {}, errors, update, destroy} = useRepeatingInvoice(organisationID, repeatingInvoiceID)

  const [form, setForm, resetForm] = useStateObject(repeatingInvoice, {emailWithPlaceholders: EMAIL_DEFAULTS})
  const [confirmDelete, setConfirmDelete, toggleConfirmDelete] = useBoolState(false)
  const [confirm, setConfirm, clearConfirm] = useStateObject(null)
  const [showEmailModal, setShowEmailModal, toggleShowEmailModal] = useBoolState()

  useDocument({title: `Repeating invoice ${repeatingInvoiceID}`})
  useEffect(() => resetForm(repeatingInvoice), [repeatingInvoice], false)
  useEffect(() => errors.email && toggleShowEmailModal(), [errors.email], false)

  const confirmUpdate = e => {
    e.preventDefault()
    setConfirm({buttonText: 'Save', onConfirm: handleUpdate})
  }

  const confirmApprove = e => {
    e.preventDefault()
    setConfirm({buttonText: 'Approve', onConfirm: handleApprove})
  }

  const handleApprove = e => {
    e.preventDefault()
    e.target.blur()
    update({state: 'approved'})
  }

  const handleUnapprove = e => {
    e.preventDefault()
    e.target.blur()
    update({state: 'draft'})
  }

  const handleDelete = ({notes}) => {
    destroy({notes}, () => history.replace(`/orgs/${organisationID}/repeating_invoices`))
  }

  const handleUpdate = e => {
    e.preventDefault()
    e.target.blur()
    update(form)
  }

  const handleChange = changes => {
    if (changes.isSendable && !form.isSendable) toggleShowEmailModal()
    setForm(changes)
  }

  const handleChangeEmail = emailWithPlaceholders => {
    setForm({emailWithPlaceholders})
    toggleShowEmailModal()
  }

  const handleCancel = e => {
    e.preventDefault()
    resetForm(repeatingInvoice)
  }

  const breadcrumbs = []
  if (repeatingInvoice.account?.id) {
    breadcrumbs.push({
      label: 'Manage accounts',
      path: `/orgs/${organisationID}/accounts`
    })
    breadcrumbs.push({
      label: repeatingInvoice.account.displayName,
      path: `/orgs/${organisationID}/accounts/${repeatingInvoice.account.id}`
    })
  } else {
    breadcrumbs.push({
      label: 'Repeating Invoices',
      path: `/orgs/${organisationID}/repeating_invoices`
    })
  }

  const confirmDateMessage = dateIsInThePast(form.nextInvoiceDate)
    ? "A 'Next invoice date' in the past can create multiple past invoices"
    : "A 'Next invoice date' for today will immediately create an invoice. No email will be sent"

  const ignoredChanges = (path, key) =>
    path.length === 0 &&
    key === 'emailWithPlaceholders' &&
    (!repeatingInvoice.emailWithPlaceholders?.subject || !repeatingInvoice.emailWithPlaceholders?.message)
  const hasChanges = diff(form, repeatingInvoice, ignoredChanges)

  const total = (repeatingInvoice.lineItems || []).reduce((sum, item) => sum + item.totalAmount, 0)

  return (
    <Fragment>
      <Flash />

      {confirm && <Confirmation {...confirm} confirm={confirmDateMessage} onClose={clearConfirm} />}
      {confirmDelete && <Deletion onConfirm={handleDelete} onClose={toggleConfirmDelete} />}

      {showEmailModal && (
        <Email
          email={form.emailWithPlaceholders}
          errors={errors.emailWithPlaceholders ? errors.emailWithPlaceholders[0] : {}}
          onSave={handleChangeEmail}
          onExit={toggleShowEmailModal}
          placeholders={{
            ['Amount']: formatNumber(total, {thousands: true}),
            ['Contact First Name']: form.account?.contactFirstName,
            ['Contact Name']: form.account?.displayName,
            ['Due Date']: moment(form.nextInvoiceDueDate).format('D MMM YYYY'),
            ['Invoice Month']: moment(form.nextInvoiceDate).format('MMMM'),
            ['Invoice Total With Currency']: formatNumber(total, {thousands: true, currency: true}),
            ['Organisation Name']: organisation.externalName
          }}>
          <h5 className='subtitle is-6'>
            <i className='fa fa-paperclip' aria-hidden='true'></i>
            &nbsp;&nbsp;
            <a
              href={`/orgs/${organisationID}/downloads/repeating_invoice.pdf?${toParams({
                repeating_invoice_id: repeatingInvoiceID,
                inline: true
              })}`}
              target='_blank'>
              Preview attached PDF
            </a>
          </h5>
        </Email>
      )}

      <Header breadcrumbs={breadcrumbs} title='Edit repeating invoice'>
        {hasChanges ? (
          <Fragment>
            <button className='button is-text is-decorationless' onClick={handleCancel}>
              Cancel
            </button>
            <button
              className='button is-primary'
              onClick={
                !dateIsInTheFuture(form.nextInvoiceDate) && form.state === 'approved' ? confirmUpdate : handleUpdate
              }>
              Save
            </button>
          </Fragment>
        ) : form.id ? (
          <Fragment>
            <a
              href={`/orgs/${organisationID}/downloads/repeating_invoice.pdf?${toParams({
                repeating_invoice_id: repeatingInvoiceID,
                inline: true
              })}`}
              target='_blank'
              className='button is-outlined is-dark'>
              Preview PDF
            </a>
            <button className='button is-danger is-outlined' onClick={toggleConfirmDelete}>
              Delete
            </button>
            {form.state === 'draft' ? (
              <button
                className='button is-primary'
                onClick={dateIsInTheFuture(form.nextInvoiceDate) ? handleApprove : confirmApprove}>
                Approve
              </button>
            ) : (
              <button className='button is-outlined is-info' onClick={handleUnapprove}>
                Revert to Draft
              </button>
            )}
          </Fragment>
        ) : null}
      </Header>
      <section className='section'>
        <div className='container'>
          <Form
            errors={errors}
            organisationID={organisationID}
            repeatingInvoice={form}
            onChange={handleChange}
            onEditEmailTemplate={toggleShowEmailModal}
          />
        </div>
      </section>
      <History itemType='RepeatingInvoice' itemId={repeatingInvoiceID} className='has-background-white-bis' />
    </Fragment>
  )
}
